/**
 * MobileLayout.tsx
 * Interface mobile dédiée pour les écrans < 768px
 * Remplace le CSS "desktop compressé" par une interface optimisée mobile
 */
import React, { useState, useCallback } from 'react';
import { useIsMobile } from '../../../hooks/useMediaQuery';
import { CanvasArea } from '../Canvas/CanvasArea';
import { PropertiesPanel } from '../PropertiesPanel/PropertiesPanel';
import { ModernSidebar } from '../Sidebar/ModernSidebar';

interface MobileLayoutProps {
  children?: React.ReactNode;
}

/**
 * Bottom Sheet pour les outils sur mobile
 * S'inspire d'Instagram Stories ou Canva App
 */
const MobileBottomSheet: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}> = ({ isOpen, onClose, title, children }) => {
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startYRef = React.useRef(0);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setIsDragging(true);
    startYRef.current = e.touches[0].clientY;
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging) return;
      const currentY = e.touches[0].clientY;
      const deltaY = currentY - startYRef.current;
      if (deltaY > 0) {
        // Seulement vers le bas
        setDragY(deltaY);
      }
    },
    [isDragging]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    if (dragY > 100) {
      // Si on a glissé de plus de 100px, fermer
      onClose();
    }
    setDragY(0);
  }, [dragY, onClose]);

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="mobile-overlay"
          onClick={onClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 998,
            animation: 'fadeIn 0.2s ease-out',
          }}
        />
      )}

      {/* Bottom Sheet */}
      <div
        className={`mobile-bottom-sheet ${isOpen ? 'open' : ''}`}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          maxHeight: '70vh',
          backgroundColor: 'var(--color-bg-panel, #1e1e1e)',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)',
          zIndex: 999,
          transform: `translateY(${isOpen ? dragY : '100%'})`,
          transition: isDragging ? 'none' : 'transform 0.3s ease-out',
          display: 'flex',
          flexDirection: 'column',
          touchAction: 'pan-y',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Handle bar pour indiquer qu'on peut glisser */}
        <div
          style={{
            width: '40px',
            height: '4px',
            backgroundColor: 'var(--color-border, #333)',
            borderRadius: '2px',
            margin: '12px auto 8px',
            cursor: 'grab',
          }}
        />

        {/* Header */}
        <div
          style={{
            padding: '16px',
            borderBottom: '1px solid var(--color-border, #333)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: 'var(--color-text-primary, #fff)',
            }}
          >
            {title}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-text-primary, #fff)',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px 8px',
            }}
            aria-label="Fermer"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {children}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </>
  );
};

/**
 * Composant MobileLayout
 * Affiche une interface optimisée pour mobile
 */
export function MobileLayout({ children }: MobileLayoutProps): React.ReactElement {
  const isMobile = useIsMobile();
  const [showTools, setShowTools] = useState(false);
  const [showProperties, setShowProperties] = useState(false);

  if (!isMobile) {
    // Sur desktop/tablette, afficher l'interface normale
    return <>{children}</>;
  }

  return (
    <div
      className="mobile-layout"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        backgroundColor: 'var(--color-bg-primary, #0a0a0a)',
      }}
    >
      {/* Canvas prend 100% de l'écran */}
      <div
        style={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          height: '100%',
        }}
      >
        <CanvasArea />
      </div>

      {/* Barre d'outils flottante en bas */}
      <div
        style={{
          position: 'fixed',
          bottom: '16px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '12px',
          zIndex: 1000,
          backgroundColor: 'var(--color-bg-panel, #1e1e1e)',
          padding: '12px',
          borderRadius: '24px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
        }}
      >
        <button
          onClick={() => {
            setShowTools(!showTools);
            setShowProperties(false);
          }}
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            border: 'none',
            backgroundColor: showTools
              ? 'var(--color-primary, #3b82f6)'
              : 'var(--color-bg-secondary, #2a2a2a)',
            color: 'var(--color-text-primary, #fff)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
          }}
          aria-label="Outils"
        >
          🎨
        </button>

        <button
          onClick={() => {
            setShowProperties(!showProperties);
            setShowTools(false);
          }}
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            border: 'none',
            backgroundColor: showProperties
              ? 'var(--color-primary, #3b82f6)'
              : 'var(--color-bg-secondary, #2a2a2a)',
            color: 'var(--color-text-primary, #fff)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
          }}
          aria-label="Propriétés"
        >
          ⚙️
        </button>
      </div>

      {/* Bottom Sheet pour les outils */}
      <MobileBottomSheet isOpen={showTools} onClose={() => setShowTools(false)} title="Outils">
        <ModernSidebar onDrawerToggle={() => {}} drawerOpen={true} onDrawerChange={() => {}} />
      </MobileBottomSheet>

      {/* Bottom Sheet pour les propriétés */}
      <MobileBottomSheet
        isOpen={showProperties}
        onClose={() => setShowProperties(false)}
        title="Propriétés"
      >
        <PropertiesPanel />
      </MobileBottomSheet>
    </div>
  );
}

export default MobileLayout;
